import axios from 'axios';
import { constants } from '../../Helpers/constantsFile';

/**
 * The two things a person can change about their own account.
 *
 * Both endpoints are the ones the phone uses. Neither is a manager-only
 * route: `updateUser` allows a person to edit themselves (it compares the
 * signed-in id against the id in the URL and only demands `manager` when they
 * differ), and `change-password` is for whoever is signed in.
 */

const auth = (token) => ({ headers: { Authorization: token } });

/**
 * Name and username.
 *
 * The server assigns a fixed set of fields and ignores anything else, so only
 * these two are sent. The user it returns HAS its business populated, which is
 * what makes it safe to put straight back into the store — every screen reads
 * the shop off `activeUser.business`.
 */
export async function updateProfile({ id, payload, token }) {
  const { data } = await axios.patch(
    `${constants.baseUrl}/users/${id}`,
    payload,
    auth(token)
  );
  return data?.data?.user;
}

/**
 * The PIN.
 *
 * The server checks the current one, saves the new one, and then — this is the
 * part worth knowing — issues a FRESH TOKEN in the response, because saving a
 * password moves `passwordChangedAt` and invalidates the one in hand.
 *
 * So the caller swaps the token and the person stays signed in. The old code
 * signed them out instead, which was not wrong exactly, just unnecessary: it
 * threw away a token the server had just handed over, and it sent a manager
 * back to the login screen in the middle of a shift.
 *
 * The user in this response is NOT populated with its business, unlike the one
 * above, so only the token is taken from it.
 *
 * @returns {Promise<string>} the new token, ready for the store
 */
export async function changePin({ id, currentPin, newPin, token }) {
  const { data } = await axios.post(
    `${constants.baseUrl}/users/change-password/${id}`,
    {
      passwordCurrent: currentPin,
      password: newPin,
      passwordConfirm: newPin,
    },
    auth(token)
  );

  return data?.token ? `Bearer ${data.token}` : '';
}
